import React from 'react';
import { Box, Skeleton } from '@/shared/ui';

const STAFF_COL_WIDTH = 140;
const DAY_COLS = 7;
const ROW_COUNT = 6;
const ROW_HEIGHT = 72;

const CHIP_LAYOUTS: ReadonlyArray<ReadonlyArray<{ left: string; width: string }>> = [
  [{ left: '12%', width: '38%' }],
  [
    { left: '8%', width: '28%' },
    { left: '48%', width: '32%' },
  ],
  [{ left: '22%', width: '42%' }],
  [],
  [
    { left: '5%', width: '24%' },
    { left: '55%', width: '30%' },
  ],
  [{ left: '30%', width: '36%' }],
];

const CalendarPageSkeleton: React.FC = () => {
  const gridTemplateColumns = `${STAFF_COL_WIDTH}px repeat(${DAY_COLS}, minmax(88px, 1fr))`;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'action.hover',
          flexShrink: 0,
        }}
      >
        <Box sx={{ p: 1.5, borderRight: 1, borderColor: 'divider' }}>
          <Skeleton variant="text" width="70%" height={20} />
        </Box>
        {Array.from({ length: DAY_COLS }, (_, i) => (
          <Box
            key={`day-${i}`}
            sx={{
              p: 1.5,
              borderRight: i < DAY_COLS - 1 ? 1 : 0,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Skeleton variant="text" width={36} height={16} />
            <Skeleton variant="circular" width={28} height={28} />
          </Box>
        ))}
      </Box>

      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {Array.from({ length: ROW_COUNT }, (_, row) => (
          <Box
            key={`row-${row}`}
            sx={{
              display: 'grid',
              gridTemplateColumns,
              minHeight: ROW_HEIGHT,
              borderBottom: row < ROW_COUNT - 1 ? 1 : 0,
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                p: 1.5,
                borderRight: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="text" width="55%" height={18} />
            </Box>
            {Array.from({ length: DAY_COLS }, (_, col) => {
              const chips = CHIP_LAYOUTS[(row + col) % CHIP_LAYOUTS.length];
              return (
                <Box
                  key={`cell-${row}-${col}`}
                  sx={{
                    position: 'relative',
                    p: 0.75,
                    borderRight: col < DAY_COLS - 1 ? 1 : 0,
                    borderColor: 'divider',
                    minHeight: ROW_HEIGHT,
                  }}
                >
                  {chips.map((chip, chipIdx) => (
                    <Skeleton
                      key={chipIdx}
                      variant="rounded"
                      height={22}
                      sx={{
                        position: 'absolute',
                        top: 10 + chipIdx * 28,
                        left: chip.left,
                        width: chip.width,
                        borderRadius: 1,
                      }}
                    />
                  ))}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default CalendarPageSkeleton;
